import React, { useState, useEffect, useRef } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useFocusTrap } from "../../../hooks/useFocusTrap";
import {
  markSourceChecked,
  MarkSourceCheckedRequest,
  MarkSourceCheckedResponse,
} from "../../../services/grantsApi";
import { sanitizeErrorMessage } from "../../../utils/error-sanitizer";
import { formatDate } from "../../../utils/date-format";
import { useToast } from "../../../contexts/ToastContext";

interface MarkSourceCheckedModalProps {
  isOpen: boolean;
  onClose: () => void;
  sourceId: string;
  sourceName: string;
  onSuccess?: () => void;
}

/**
 * Modal for marking a MANUAL source as checked.
 * Allows adding an optional note about the check.
 * @see docs/specifications/2026-01-20-manual-source-phase3-frontend.md Section 4
 */
const MarkSourceCheckedModal: React.FC<MarkSourceCheckedModalProps> = ({
  isOpen,
  onClose,
  sourceId,
  sourceName,
  onSuccess,
}) => {
  const queryClient = useQueryClient();
  const { addToast } = useToast();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  // Focus trap for accessibility
  useFocusTrap(modalRef, isOpen);

  const [note, setNote] = useState("");
  const [successData, setSuccessData] = useState<MarkSourceCheckedResponse | null>(null);

  const mutation = useMutation({
    mutationFn: (data: MarkSourceCheckedRequest) =>
      markSourceChecked(sourceId, data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ["grants-sources"] });
      setSuccessData(response);
      addToast({
        message: "Źródło zostało oznaczone jako sprawdzone",
        type: "success",
        duration: 3000,
      });
      onSuccess?.();
    },
    onError: (error: any) => {
      addToast({
        message: sanitizeErrorMessage(error),
        type: "error",
        duration: 5000,
      });
    },
  });

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setNote("");
      setSuccessData(null);
      mutation.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Focus textarea when modal opens
  useEffect(() => {
    if (isOpen && !successData) {
      setTimeout(() => textareaRef.current?.focus(), 100);
    }
  }, [isOpen, successData]);

  // Handle ESC key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !mutation.isPending) {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
    }
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose, mutation.isPending]);

  const handleSubmit = () => {
    mutation.mutate({ note: note.trim() || undefined });
  };

  const handleClose = () => {
    if (!mutation.isPending) {
      onClose();
    }
  };

  if (!isOpen) return null;

  const isLoading = mutation.isPending;

  // Show success state with next check time
  if (successData) {
    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
        role="dialog"
        aria-modal="true"
        aria-labelledby="success-title"
      >
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
          <div className="px-6 py-4 border-b border-neutral-200">
            <h2 id="success-title" className="text-lg font-medium text-neutral-900">
              Źródło oznaczone jako sprawdzone
            </h2>
          </div>

          <div className="px-6 py-4 space-y-4">
            <div className="flex items-center gap-3 p-4 bg-green-50 rounded-lg">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <p className="font-medium text-green-800">{sourceName}</p>
                <p className="text-sm text-green-600">
                  Sprawdzono: {formatDate(successData.checkedAt)}
                </p>
              </div>
            </div>

            <p className="text-sm text-neutral-600">
              Następne przypomnienie:{" "}
              <span className="font-medium">{formatDate(successData.nextCheckAt)}</span>
            </p>
          </div>

          <div className="px-6 py-4 border-t border-neutral-200 flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700"
            >
              Zamknij
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Render form
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      onClick={(e) => {
        if (e.target === e.currentTarget && !isLoading) {
          onClose();
        }
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      <div ref={modalRef} className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="px-6 py-4 border-b border-neutral-200 flex items-center justify-between">
          <h2 id="modal-title" className="text-lg font-medium text-neutral-900">
            Oznacz źródło jako sprawdzone
          </h2>
          <button
            type="button"
            onClick={handleClose}
            disabled={isLoading}
            className="text-neutral-400 hover:text-neutral-500 disabled:opacity-50"
            aria-label="Zamknij"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="px-6 py-4 space-y-4">
          <div className="p-4 bg-neutral-50 rounded-lg">
            <p className="text-sm font-medium text-neutral-900">
              Źródło: {sourceName}
            </p>
          </div>

          <div>
            <label
              htmlFor="check-note"
              className="block text-sm font-medium text-neutral-700 mb-1"
            >
              Notatka (opcjonalne)
            </label>
            <textarea
              ref={textareaRef}
              id="check-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              maxLength={500}
              className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="Brak nowych artykułów na stronie"
              disabled={isLoading}
            />
            <p className="mt-1 text-xs text-neutral-500">
              {note.length}/500 znaków
            </p>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-neutral-200 flex flex-col sm:flex-row justify-end gap-3">
          <button
            type="button"
            onClick={handleClose}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-neutral-700 bg-neutral-100 rounded-md hover:bg-neutral-200 disabled:opacity-50"
          >
            Anuluj
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {isLoading && (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
            )}
            {isLoading ? "Zapisywanie..." : "Potwierdź"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MarkSourceCheckedModal;
